import { useMemo } from 'react';

import { sumBy } from 'lodash';
import { CollectionQueryOptions, Nullable } from '../types';
import { Company, Quote, SanitizedQuoteConversionRate } from '../models';
import { ProductType, PurchaseStatus, QuoteStatus } from '../enums';
import { getQuoteProductExtendedPrice } from '../utils/quote';
import { CurrencyFormatter } from '../utils';
import { getCompanyLocation } from '../utils/company';
import { useDataCollection } from './useDataCollection';

export function useQuoteUserConversionRates(
  companyId: string,
  quotes: Quote[],
  productTypes?: Nullable<ProductType[]>,
  options?: Pick<CollectionQueryOptions, 'pagination' | 'sorting'>
) {
  const quoteConversionRates = useMemo(() => {
    const result = quotes.reduce((acc, cur) => {
      const filteredProducts = cur.products?.filter(
        (p) =>
          !productTypes ||
          (p.productType && productTypes.includes(p.productType))
      );

      if (
        !cur.companyId ||
        cur.companyId !== companyId ||
        !filteredProducts?.length
      ) {
        return acc;
      }

      const companyLocation = getCompanyLocation({
        accountNumber: cur.companyAccountNumber,
      } as Company);
      const currencyFormatter = new CurrencyFormatter(
        companyLocation,
        Number.MAX_VALUE
      );

      const record = acc[cur.customerId] || new SanitizedQuoteConversionRate();

      if (!record.id || !record.name) {
        record.id = cur.customerId;
        record.name = cur.customerName;
      }

      record.totalQuoted += sumBy(filteredProducts, (p) =>
        getQuoteProductExtendedPrice(p, cur.measurementUnits, cur.priceUnits)
      );

      record.quoted += filteredProducts.length;

      if (cur.status === QuoteStatus.Expired) {
        record.expired += filteredProducts.length;
      } else {
        filteredProducts.forEach((p) => {
          if (p.purchaseStatus === PurchaseStatus.Lost) {
            record.lost++;
          } else if (p.purchaseStatus === PurchaseStatus.Won) {
            record.won++;
            record.totalWon += getQuoteProductExtendedPrice(
              p,
              cur.measurementUnits,
              cur.priceUnits
            );
          }
        });
      }

      record.rate = record.quoted
        ? Math.round((record.won / record.quoted) * 100)
        : 0;

      record.totalWonRate =
        record.totalWon && record.totalQuoted
          ? Math.round((record.totalWon / record.totalQuoted) * 100)
          : 0;
      record.totalQuotedFormatted = currencyFormatter.format(
        record.totalQuoted
      );
      record.totalWonFormatted = currencyFormatter.format(record.totalWon);

      acc[cur.customerId] = record;
      return acc;
    }, {} as Record<string, SanitizedQuoteConversionRate>);

    return Object.values(result);
  }, [companyId, productTypes, quotes]);

  return useDataCollection(
    quoteConversionRates,
    options?.sorting,
    options?.pagination
  );
}
